// Local imports
import React, { ChangeEvent, DragEvent, useCallback, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Container, Divider, Grid, TextField, Typography } from '@mui/material';
import { ImportRecipeForm, ImportRecipeFormSchema, ImportRecipeRequest, ProcessUploadRequest } from '../../services/api/models';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, useNavigate } from 'react-router-dom';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { Api } from '../../services/api';
import { CloudUpload } from '@mui/icons-material';

// Component definition
function ImportPage() {
  const [importError, setImportError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const methods = useForm<ImportRecipeForm>({
      resolver: zodResolver(ImportRecipeFormSchema), // Apply the zodResolver
      defaultValues: {},
    });
    const {
      register,
      handleSubmit,
    } = methods;

  const onSubmit = async (form: ImportRecipeForm) => {
    try {
      setLoading(true);

      // update
      const request: ImportRecipeRequest = {
        url: form.url,
      }

      Api.Recipe.import(request)
        .then(response => {
          console.log(response.data);

          setLoading(false);

          switch (response.data.status) {
            case 'IMPORTED':
              navigate(`/recipe/${response.data.recipe_id}`);
              setImportError('');
              break;
            case 'NOT_A_RECIPE':
              setImportError('Cocinero could not find a recipe at that url');
              break;
            case 'HTTP_ERROR':
              setImportError('Cocinero couldn\'t reach this url.');
              break;
            case 'FAILED':
            default:
              setImportError('Unable to import recipe.');
          }


        }).catch(error => {
          console.warn(error);
          setLoading(false);
          setImportError('Error importing recipe')
        });


      console.log(form);
    } catch (error: unknown) {
      console.log(error);
      setImportError('Error importing recipe')
      setLoading(false);
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    event.dataTransfer.dropEffect = 'copy'; // or 'move'
    event.dataTransfer.effectAllowed = 'all'; // or 'all'

    console.log(event.dataTransfer);

    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      // For Chrome and other browsers that populate files directly
      uploadFile(event.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    console.log(selectedFile);
    uploadFile(selectedFile);
  };

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleDragEnter = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const uploadFile = async (file: File | null) => {
    if (file) {
      console.log('File changed:', file.name);
      console.log('Content Type:', file.type);
      // You can add validation, preview generation, or upload logic here.

      setLoading(true);

      let signedUrl;
      let fileId;

      try {
        const response = await Api.Recipe.get_signed_upload();
        signedUrl = response.data.url;
        fileId = response.data.file_id;

        console.log('signed_url: ' + signedUrl);
        console.log('file_id: ' + fileId);

      } catch (error: unknown) {
        console.warn(error);
        setLoading(false);
        setImportError('Error importing recipe')
      }

      try {
        if (!signedUrl || !fileId) {
          setLoading(false);
          return;
        }

        await Api.S3Upload.upload(signedUrl, file)
      } catch (error: unknown) {
        console.warn(error);
        setLoading(false);
        setImportError('Error importing recipe')
      }

      try {
        const request: ProcessUploadRequest = {
          file_id: fileId,
        }

        const resp = await Api.Recipe.process(request)
        console.log(resp.data);

          setLoading(false);

          switch (resp.data.status) {
            case 'IMPORTED':
              navigate(`/recipe/${resp.data.recipe_id}`);
              setImportError('');
              break;
            case 'NOT_A_RECIPE':
              setImportError('Cocinero could not import a recipe from that file');
              break;
            case 'HTTP_ERROR':
              setImportError('Cocinero couldn\'t read this file.');
              break;
            case 'FAILED':
            default:
              setImportError('Unable to import recipe.');
          }
      } catch (error: unknown) {
        setLoading(false);
        setImportError('Error importing recipe')
      }
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h2" gutterBottom>
        Import recipe from the web
      </Typography>

      {importError && (
        <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
          {importError}
        </Alert>
      )}

      <FormProvider {...methods}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid item xs={10}>
              <TextField
                margin="normal"
                required
                fullWidth
                label="https://"
                type="text"
                autoComplete="text"
                autoFocus
                {...register('url')}
              />
            </Grid>
            <Grid item xs={2}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                startIcon={<CloudDownloadIcon />}
                sx={{ mt: 3, mb: 2 }}
              >
                Import
              </Button>
            </Grid>
          </Grid>
        </Form>
      </FormProvider>

      <Divider sx={{ my: 2 }}> {/* 'my' adds vertical margin */}
        <Typography variant="body2" color="text.secondary">
          OR
        </Typography>
      </Divider>

      <Box
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        sx={{
          border: '2px dashed #90caf9',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          color: '#90caf9',
          cursor: 'pointer',
          mb: 2,
        }}
        onClick={() => document.getElementById('fileInput')!.click()}
      >
        <CloudUpload sx={{ fontSize: 48, mb: 1 }} />
        <Typography variant="h6">Drag & Drop files here</Typography>
        <Typography variant="body2" color="textSecondary">or click to select</Typography>
        <input
          id="fileInput"
          type="file"
          hidden
          onChange={handleFileChange}
        />
      </Box>

      {loading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)', // Optional: Add a semi-transparent background
            zIndex: (theme) => theme.zIndex.modal + 1, // Ensure it's on top
          }}
        >
          <CircularProgress color="primary" />
        </Box>
      )}
    </Container>
  )
}

// Default export
export default ImportPage;
